/** Virtual Try-On outfit_scope — FULL_LOOK_AUTO default + legacy migration. */

export type OutfitScope = 'FULL_LOOK_AUTO' | 'AUTO' | 'UPPER_ONLY' | 'LOWER_ONLY' | 'ONE_PIECE'
export type OutfitScopeSource = 'DEFAULT' | 'USER'

/** Canonical product default. Wire alias AUTO == FULL_LOOK_AUTO. */
export const DEFAULT_OUTFIT_SCOPE: OutfitScope = 'FULL_LOOK_AUTO'

export const ALL_OUTFIT_SCOPES: OutfitScope[] = [
  'FULL_LOOK_AUTO',
  'UPPER_ONLY',
  'LOWER_ONLY',
  'ONE_PIECE',
]

/** Legacy CatVTON-shaped category (still accepted on read / wire for older jobs). */
export type LegacyTryOnCategory = 'upper_body' | 'lower_body' | 'dress'

const LEGACY_TO_SCOPE: Record<string, OutfitScope> = {
  upper_body: 'UPPER_ONLY',
  tops: 'UPPER_ONLY',
  lower_body: 'LOWER_ONLY',
  bottoms: 'LOWER_ONLY',
  dress: 'ONE_PIECE',
  'one-pieces': 'ONE_PIECE',
  one_pieces: 'ONE_PIECE',
  auto: 'FULL_LOOK_AUTO',
  full_look_auto: 'FULL_LOOK_AUTO',
}

const SCOPE_TO_LEGACY: Record<string, LegacyTryOnCategory> = {
  FULL_LOOK_AUTO: 'dress',
  AUTO: 'dress',
  UPPER_ONLY: 'upper_body',
  LOWER_ONLY: 'lower_body',
  ONE_PIECE: 'dress',
}

export type OutfitScopeSelectorMode = 'HIDDEN' | 'FULL'

export interface OutfitScopeControls {
  outfit_scope_selector: OutfitScopeSelectorMode
  supported_outfit_scopes: OutfitScope[]
  default_outfit_scope: OutfitScope
  capability_gap_code?: string | null
}

export function canonicalizeOutfitScope(scope: string): OutfitScope {
  const s = scope.trim().toUpperCase()
  if (s === 'AUTO') return 'FULL_LOOK_AUTO'
  if (s === 'FULL_LOOK_AUTO' || s === 'UPPER_ONLY' || s === 'LOWER_ONLY' || s === 'ONE_PIECE') {
    return s
  }
  return DEFAULT_OUTFIT_SCOPE
}

export function isOutfitScope(value: unknown): value is OutfitScope {
  return (
    value === 'FULL_LOOK_AUTO' ||
    value === 'AUTO' ||
    value === 'UPPER_ONLY' ||
    value === 'LOWER_ONLY' ||
    value === 'ONE_PIECE'
  )
}

/**
 * Resolve outfit_scope from node fields.
 * Legacy implicit upper_body / dress (no USER source) → FULL_LOOK_AUTO.
 * Explicit USER + upper_body → UPPER_ONLY preserved.
 */
export function normalizeOutfitScopeFromFields(fields: Record<string, unknown>): {
  outfitScope: OutfitScope
  outfitScopeSource: OutfitScopeSource
  /** CatVTON-compatible wire category for submit payload backward compat. */
  legacyCategory: LegacyTryOnCategory
} {
  const sourceRaw = String(fields.outfitScopeSource || '').toUpperCase()
  const source: OutfitScopeSource = sourceRaw === 'USER' ? 'USER' : 'DEFAULT'

  if (isOutfitScope(fields.outfitScope)) {
    const canon = canonicalizeOutfitScope(fields.outfitScope)
    return {
      outfitScope: canon,
      outfitScopeSource: source,
      legacyCategory: SCOPE_TO_LEGACY[canon],
    }
  }

  const cat = String(fields.category || '').trim().toLowerCase()
  if (cat && LEGACY_TO_SCOPE[cat]) {
    const mapped = LEGACY_TO_SCOPE[cat]
    if (source === 'USER') {
      return { outfitScope: mapped, outfitScopeSource: 'USER', legacyCategory: SCOPE_TO_LEGACY[mapped] }
    }
    // Silent registry defaults (tops / dress) → FULL_LOOK_AUTO
    if (cat === 'upper_body' || cat === 'tops' || cat === 'dress') {
      return {
        outfitScope: 'FULL_LOOK_AUTO',
        outfitScopeSource: 'DEFAULT',
        legacyCategory: SCOPE_TO_LEGACY.FULL_LOOK_AUTO,
      }
    }
    return { outfitScope: mapped, outfitScopeSource: 'DEFAULT', legacyCategory: SCOPE_TO_LEGACY[mapped] }
  }

  return {
    outfitScope: DEFAULT_OUTFIT_SCOPE,
    outfitScopeSource: 'DEFAULT',
    legacyCategory: SCOPE_TO_LEGACY.FULL_LOOK_AUTO,
  }
}

export function legacyCategoryForScope(scope: OutfitScope): LegacyTryOnCategory {
  return SCOPE_TO_LEGACY[canonicalizeOutfitScope(scope)]
}

/** When Provider Option does not advertise controls, assume FULL. */
export function resolveOutfitScopeControls(
  option: { outfit_scope_controls?: OutfitScopeControls | null } | null | undefined,
): OutfitScopeControls {
  const c = option?.outfit_scope_controls
  if (c && (c.outfit_scope_selector === 'HIDDEN' || c.outfit_scope_selector === 'FULL')) {
    const supported = (c.supported_outfit_scopes || [])
      .map((s) => (isOutfitScope(s) ? canonicalizeOutfitScope(s) : null))
      .filter((s): s is OutfitScope => s != null)
    // Prefer canonical FULL_LOOK_AUTO; drop wire AUTO duplicates.
    const uniq = Array.from(new Set(supported.map((s) => (s === 'AUTO' ? 'FULL_LOOK_AUTO' : s))))
    return {
      outfit_scope_selector: 'FULL',
      supported_outfit_scopes: uniq.length ? uniq : ALL_OUTFIT_SCOPES,
      default_outfit_scope: isOutfitScope(c.default_outfit_scope)
        ? canonicalizeOutfitScope(c.default_outfit_scope)
        : 'FULL_LOOK_AUTO',
      capability_gap_code: c.capability_gap_code ?? null,
    }
  }
  return {
    outfit_scope_selector: 'FULL',
    supported_outfit_scopes: ALL_OUTFIT_SCOPES,
    default_outfit_scope: 'FULL_LOOK_AUTO',
  }
}

/**
 * Infer FULL controls for Max product labels (scope is product-layer, not vendor category).
 */
export function inferOutfitScopeControlsFromDisplayName(displayName: string | null | undefined): OutfitScopeControls | null {
  const n = (displayName || '').trim()
  if (
    n === '快速换装' ||
    n === '高保真换装' ||
    n === '标准换装' ||
    n.includes('Max') ||
    n.includes('快速换装') ||
    n.includes('高保真换装') ||
    n.includes('标准换装') ||
    n.includes('v1.6') ||
    n.includes('服装换装') ||
    n.includes('整套')
  ) {
    return {
      outfit_scope_selector: 'FULL',
      supported_outfit_scopes: ALL_OUTFIT_SCOPES,
      default_outfit_scope: 'FULL_LOOK_AUTO',
    }
  }
  return null
}
